import { useCallback, useEffect, useState } from 'react'
import {
  fetchAccountCustomFields,
  fetchContactCustomFields,
  listCustomFieldDefs,
  saveAccountCustomFields,
  saveContactCustomFields,
} from '../../lib/outreach/api'
import { coerceValue, diffValues } from '../../lib/outreach/customFields'
import type { CustomFieldDef, CustomFieldValue, CustomFieldValues } from '../../lib/outreach/types'
import { toast } from '../../lib/toast'
import { Button } from '../../shared/ui'
import type { IslandProps } from '../registry'

/**
 * Custom fields of one contact or account, shown inside the record drawer.
 * The host mounts it with `customFieldsTarget: { entity, id }`. It renders
 * nothing when the business has no fields for that entity.
 */

type FormValues = Record<string, string | string[]>

function toForm(defs: readonly CustomFieldDef[], values: CustomFieldValues): FormValues {
  const out: FormValues = {}
  for (const d of defs) {
    const v: CustomFieldValue | undefined = values[d.key]
    if (d.type === 'multi_select') out[d.key] = Array.isArray(v) ? v : []
    else out[d.key] = v === null || v === undefined ? '' : String(v)
  }
  return out
}

export default function CustomFieldsPanel({ user, customFieldsTarget }: IslandProps) {
  const [defs, setDefs] = useState<CustomFieldDef[]>([])
  const [saved, setSaved] = useState<CustomFieldValues>({})
  const [form, setForm] = useState<FormValues>({})
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const target = customFieldsTarget

  const load = useCallback(async () => {
    if (!target || !user) return
    setStatus('loading')
    try {
      const [fieldDefs, values] = await Promise.all([
        listCustomFieldDefs(target.entity),
        target.entity === 'contact'
          ? fetchContactCustomFields(target.id, user.id)
          : fetchAccountCustomFields(target.id),
      ])
      setDefs(fieldDefs)
      setSaved(values)
      setForm(toForm(fieldDefs, values))
      setStatus('ready')
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
      setStatus('error')
    }
  }, [target, user])

  useEffect(() => {
    void load()
  }, [load])

  if (!target || !user) return null
  if (status === 'error') {
    return (
      <div className="settings-hint" role="alert">
        Custom fields could not load: {error}
      </div>
    )
  }
  if (status === 'loading' || defs.length === 0) return null

  const set = (key: string, value: string | string[]) => setForm((f) => ({ ...f, [key]: value }))

  const save = async () => {
    const next: CustomFieldValues = {}
    for (const d of defs) {
      const res = coerceValue(d, form[d.key])
      if (!res.ok) {
        toast(res.error, 'error')
        return
      }
      next[d.key] = res.value
    }
    const patch = diffValues(saved, next)
    if (Object.keys(patch).length === 0) {
      toast('Nothing changed')
      return
    }
    setBusy(true)
    try {
      if (target.entity === 'contact') await saveContactCustomFields(target.id, patch)
      else await saveAccountCustomFields(target.id, patch)
      const merged: CustomFieldValues = { ...saved }
      for (const [k, v] of Object.entries(patch)) {
        if (v === null) delete merged[k]
        else merged[k] = v
      }
      setSaved(merged)
      toast('Custom fields saved', 'success')
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e), 'error')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div>
      <div className="section-title" style={{ marginTop: 20 }}>
        Custom fields
      </div>
      {defs.map((d) => {
        const id = `cfp-${d.key}`
        const value = form[d.key]
        return (
          <div className="dfield" key={d.id}>
            <label className="dfield-label" htmlFor={id}>
              {d.label}
            </label>
            {d.type === 'select' ? (
              <select
                id={id}
                className="inline-edit"
                value={typeof value === 'string' ? value : ''}
                onChange={(e) => set(d.key, e.target.value)}
              >
                <option value="">Not set</option>
                {d.options.map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
            ) : d.type === 'multi_select' ? (
              <div id={id} role="group" aria-label={d.label}>
                {d.options.map((o) => {
                  const list = Array.isArray(value) ? value : []
                  return (
                    <label key={o} style={{ display: 'block', fontSize: 13 }}>
                      <input
                        type="checkbox"
                        checked={list.includes(o)}
                        onChange={(e) =>
                          set(d.key, e.target.checked ? [...list, o] : list.filter((x) => x !== o))
                        }
                      />{' '}
                      {o}
                    </label>
                  )
                })}
              </div>
            ) : (
              <input
                id={id}
                className="inline-edit"
                type={
                  d.type === 'number'
                    ? 'number'
                    : d.type === 'date'
                      ? 'date'
                      : d.type === 'url'
                        ? 'url'
                        : 'text'
                }
                step={d.type === 'number' ? 'any' : undefined}
                value={typeof value === 'string' ? value : ''}
                onChange={(e) => set(d.key, e.target.value)}
              />
            )}
          </div>
        )
      })}
      <Button variant="secondary" disabled={busy} onClick={() => void save()}>
        Save custom fields
      </Button>
    </div>
  )
}
